import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IDataNoPagination } from '../types/shared';

interface ExportParams {
  dateStart?: string;
  dateEnd?: string;
  codiEmp?: number;
}

export interface INotaDuplicada {
  id: number;
  codi_emp: number;
  codi_nota: number;
  numero_nota: string;
  serie: string | null;
  modelo: number | null;
  codi_clifor: number | null;
  cgce_clifor: string;
  nome_clifor: string | null;
  vcontabil: number;
  data_considerar: string | null;
  emissao: string | null;
  tipo_nota: string | null;
  grupo_duplicata: string;
  quantidade_duplicadas: number;
  created_at: string;
  updated_at: string;
}

export interface INotasDuplicadasInsights {
  total_empresas_com_duplicadas: number;
  total_notas_duplicadas: number;
  total_entrada: number;
  total_saida: number;
}

export interface INotasDuplicadasEmpresaResumo {
  codi_emp: number;
  quantidade_duplicadas: number;
  quantidade_entrada: number;
  quantidade_saida: number;
  nome_empresa: string | null;
  razao_social: string | null;
  cnpj: string | null;
}

export interface IClifor {
  nome: string;
  documento: string;
}

export const useNotasDuplicadas = () => {
  const getInsights = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INotasDuplicadasInsights>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<INotasDuplicadasInsights>>(
      '/api/v1/notas-duplicadas/insights/',
      { params: filters },
    );
    return res.data;
  };

  const getEmpresasResumo = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INotasDuplicadasEmpresaResumo[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<INotasDuplicadasEmpresaResumo[]>>(
      '/api/v1/notas-duplicadas/empresas/',
      { params: filters },
    );
    return res.data;
  };

  const getNotasByEmpresa = async (
    key: QueryFunctionContext,
  ): Promise<IDataNoPagination<INotaDuplicada[]>> => {
    const { codi_emp: codiEmp, ...filters } = (key.queryKey?.[1] as any) ?? {};
    const res = await api.get<IDataNoPagination<INotaDuplicada[]>>(
      `/api/v1/notas-duplicadas/empresa/${codiEmp}/`,
      { params: filters },
    );
    return res.data;
  };

  const exportAllDuplicadas = async (params: ExportParams): Promise<void> => {
    const response = await api.get('/api/v1/notas-duplicadas/export/', {
      params: {
        date_start: params.dateStart,
        date_end: params.dateEnd,
      },
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const periodo =
      params.dateStart && params.dateEnd ? `${params.dateStart}_a_${params.dateEnd}` : 'todos';
    link.setAttribute('download', `notas_duplicadas_${periodo}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportByEmpresa = async (params: ExportParams): Promise<void> => {
    if (!params.codiEmp) return;

    const response = await api.get(`/api/v1/notas-duplicadas/export/empresa/${params.codiEmp}/`, {
      params: {
        date_start: params.dateStart,
        date_end: params.dateEnd,
      },
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const periodo =
      params.dateStart && params.dateEnd ? `${params.dateStart}_a_${params.dateEnd}` : 'todos';
    link.setAttribute('download', `notas_duplicadas_empresa_${params.codiEmp}_${periodo}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const getClifores = async (key: QueryFunctionContext): Promise<IDataNoPagination<IClifor[]>> => {
    const filters = key.queryKey?.[1] ?? {};
    const res = await api.get<IDataNoPagination<IClifor[]>>('/api/v1/notas-duplicadas/clifores/', {
      params: filters,
    });
    return res.data;
  };

  return {
    getInsights,
    getEmpresasResumo,
    getNotasByEmpresa,
    getClifores,
    exportAllDuplicadas,
    exportByEmpresa,
  };
};
